/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { asyncGen } from "../index.js";
import { HTTPClientTransport } from "../remote/http.js";
import { ProxyClient } from "../remote/proxy.js";
import { runLocally } from "./local.js";
import { configureSecretAsking } from "./secrets.js";
import { runInWorker } from "./worker.js";
const configureKits = async (config) => {
    // If a proxy is configured, add the proxy kit to the list of kits.
    if (!config.proxy)
        return config.kits;
    const kits = [];
    for (const proxyConfig of config.proxy) {
        if (typeof proxyConfig === "function") {
            const config = await proxyConfig();
            if (!config)
                continue;
            kits.push(await proxyConfig());
        }
        else {
            switch (proxyConfig.location) {
                case "http": {
                    if (!proxyConfig.url) {
                        throw new Error("No node proxy server URL provided.");
                    }
                    const proxyClient = new ProxyClient(new HTTPClientTransport(proxyConfig.url));
                    kits.push(proxyClient.createProxyKit(proxyConfig.nodes));
                    break;
                }
                default: {
                    throw new Error("Only HTTP node proxy server is supported at this time.");
                }
            }
        }
    }
    return [...kits, ...config.kits];
};
export async function* run(config) {
    if (!config.remote) {
        yield* asyncGen(async (next) => {
            const kits = configureSecretAsking(config.interactiveSecrets, await configureKits(config), next);
            for await (const data of runLocally(config, kits)) {
                await next(data);
            }
        });
    }
    else if (config.remote.type === "worker") {
        const workerURL = config.remote && config.remote.url;
        if (!workerURL) {
            throw new Error("Worker harness requires a worker URL");
        }
        yield* runInWorker(workerURL, config);
    }
    else {
        throw new Error(`Unsupported harness configuration: ${JSON.stringify(config, null, 2)}`);
    }
}
//# sourceMappingURL=run.js.map